import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { ArrowLeft, Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import JobStatusBanner from '@/components/analysis/JobStatusBanner';
import ModuleProgressList from '@/components/analysis/ModuleProgressList';
import ResultsLayout from '@/components/analysis/ResultsLayout';
import { findingsAtom } from '@/hooks/atoms';
import { useJobPolling } from '@/hooks/useJobPolling';
import { useResults, useExportReport } from '@/hooks/useResults';
import { useAuthenticatedMedia } from '@/hooks/useAuthenticatedMedia';
import { apiFetch } from '@/lib/api';
type AnalysisMode = 'standard' | 'deep';

const TERMINAL_STATUSES = new Set(['complete', 'partial_complete', 'failed', 'canceled']);

export default function AnalyzePage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { job, moduleRuns } = useJobPolling(jobId);
  const { data: results, fetchResults, reset: resetResults } = useResults(jobId);
  const { exportReport, loading: exportLoading } = useExportReport(jobId);
  const setFindings = useSetAtom(findingsAtom);
  const [rerunLoading, setRerunLoading] = useState(false);

  const isTerminal = job?.status && TERMINAL_STATUSES.has(job.status);
  const isComplete = job?.status === 'complete' || job?.status === 'partial_complete';
  const mediaUrl = useAuthenticatedMedia(jobId, Boolean(isComplete));

  const handleRerun = useCallback(
    async (mode: AnalysisMode) => {
      if (!jobId || rerunLoading) return;
      setRerunLoading(true);
      try {
        const response = await apiFetch(`/api/jobs/${jobId}/rerun`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode }),
        });
        if (!response.ok) throw new Error('Failed to re-run analysis');
        // Clear previous results so the UI shows progress view
        resetResults();
        setFindings([]);
      } catch (err) {
        console.error('Re-run failed:', err);
      } finally {
        setRerunLoading(false);
      }
    },
    [jobId, rerunLoading, resetResults, setFindings],
  );

  // Fetch results when job reaches terminal state
  useEffect(() => {
    if (isTerminal && !results) {
      fetchResults();
    }
  }, [isTerminal, results, fetchResults]);

  // Sync findings to Jotai for filtering
  useEffect(() => {
    if (results?.findings) {
      setFindings(results.findings);
    }
  }, [results?.findings, setFindings]);

  // Loading state
  if (!job) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading analysis...</p>
        </div>
      </div>
    );
  }

  // Results view
  if (isComplete && results) {
    return (
      <div className="relative">
        {/* Top bar with actions */}
        <div className="absolute top-2 right-4 z-20 flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border rounded-lg shadow-sm p-0.5">
            <Button
              variant={rerunLoading ? 'ghost' : 'outline'}
              size="sm"
              onClick={() => handleRerun('standard')}
              disabled={rerunLoading}
              className="text-xs"
            >
              {rerunLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Re-run Standard
            </Button>
            <Button
              variant={rerunLoading ? 'ghost' : 'default'}
              size="sm"
              onClick={() => handleRerun('deep')}
              disabled={rerunLoading}
              className="text-xs"
            >
              {rerunLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Re-run Deep
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={exportReport} disabled={exportLoading}>
            {exportLoading ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5 mr-1.5" />
            )}
            Export JSON
          </Button>
        </div>

        <ResultsLayout
          findings={results.findings}
          moduleRuns={results.moduleRuns}
          summary={results.summary}
          metadataSummary={results.metadataSummary}
          imageWidth={job.mediaAsset?.width}
          imageHeight={job.mediaAsset?.height}
          previewUrl={mediaUrl}
          mediaType={job.mediaAsset?.mediaType}
        />
      </div>
    );
  }

  // Progress/status view
  return (
    <div className="max-w-2xl mx-auto py-12 px-4">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" />
        Back to Upload
      </Link>

      <Card>
        <CardContent className="p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">{job.mediaAsset?.filename || `Job ${jobId}`}</h2>
            <p className="text-xs text-muted-foreground">
              Job ID: {jobId} &middot; Mode: {job.mode || 'standard'}
            </p>
          </div>

          <JobStatusBanner status={job.status} progressPct={job.progressPct} errorMessage={job.errorMessage} />

          <ModuleProgressList moduleRuns={moduleRuns} />

          {job.status === 'failed' && (
            <div className="pt-4 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleRerun(job.mode || 'standard')} disabled={rerunLoading}>
                {rerunLoading ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Retry Analysis
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  New Upload
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
